import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "aws-amplify/auth";
import { useLanguage } from "../LanguageContext";
import { getProProfile } from "../api";
import LanguageSelector from "./LanguageSelector";
import BackgroundPicker from "./BackgroundPicker";
import ThemeToggle from "./ThemeToggle";

interface Props {
  role?: "client" | "pro" | "admin";
}

export default function HamburgerMenu({ role = "client" }: Props) {
  const [open, setOpen] = useState(false);
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [businessName, setBusinessName] = useState("");
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    try {
      const k = Object.keys(localStorage).find(key => key.includes("idToken"));
      if (k) {
        const token = localStorage.getItem(k);
        if (token) {
          const payload = JSON.parse(atob(token.split(".")[1]));
          setUserEmail(payload.email || "");
          setUserName(payload.name || payload.email?.split("@")[0] || "User");
        }
      }
    } catch {}

    if (role === "pro") {
      getProProfile().then(p => {
        if (p?.businessName) setBusinessName(p.businessName);
        if (p?.name) setUserName(p.name);
      }).catch(() => {});
    }
  }, [role]);

  async function handleSignOut() {
    await signOut();
    localStorage.clear();
    window.location.href = "/";
  }

  function go(path: string) {
    setOpen(false);
    navigate(path);
  }

  const sections = [
    {
      title: "Menu",
      items: [
        { icon: "⚙️", label: "Settings", path: role === "pro" ? "/pro/edit" : "/profile" },
        { icon: "💳", label: "Payments", path: role === "pro" ? "/pro/earnings" : "/profile" },
        { icon: "🔔", label: "Notifications", path: "/notifications" },
        { icon: "🛡️", label: "Support", path: "/support" },
      ]
    },
  ];

  const displayName = role === "pro" && businessName ? businessName : userName;
  const accountLabel = role === "pro" ? "Pro Account" : role === "admin" ? "Admin Account" : "Customer";
  const initial = (displayName || "U").charAt(0).toUpperCase();

  return (
    <>
      {/* Hamburger button */}
      <button onClick={() => setOpen(true)}
        className="w-10 h-10 rounded-full bg-gray-50 hover:bg-gray-100 flex items-center justify-center text-navy transition-colors"
        aria-label="Open menu">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <line x1="4" y1="6" x2="20" y2="6"/>
          <line x1="4" y1="12" x2="20" y2="12"/>
          <line x1="4" y1="18" x2="20" y2="18"/>
        </svg>
      </button>

      {/* Overlay */}
      {open && (
        <div onClick={() => setOpen(false)}
          className="fixed inset-0 bg-black bg-opacity-40 z-40 transition-opacity"/>
      )}

      {/* Drawer */}
      <div className={`fixed top-0 left-0 h-full w-80 max-w-[85%] bg-white z-50 shadow-2xl transform transition-transform duration-300 flex flex-col ${open ? "translate-x-0" : "-translate-x-full"}`}>

        {/* Header with close */}
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2">
            <svg width="32" height="32" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="mLg1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#4C6EF5"/><stop offset="100%" stopColor="#1A3CE8"/></linearGradient>
                <linearGradient id="mLg2" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#20C997"/><stop offset="100%" stopColor="#0E8C6A"/></linearGradient>
                <linearGradient id="mBg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#3B5BDB"/><stop offset="100%" stopColor="#0E8C6A"/></linearGradient>
              </defs>
              <rect width="52" height="52" rx="14" fill="url(#mBg)"/>
              <g transform="translate(4,5)">
                <path d="M11,0 C4.9,0 0,4.9 0,11 C0,17.1 11,28 11,28 C11,28 22,17.1 22,11 C22,4.9 17.1,0 11,0 Z" fill="white" opacity="0.95"/>
                <circle cx="11" cy="10" r="5" fill="url(#mLg1)"/>
                <circle cx="11" cy="10" r="2.5" fill="white"/>
              </g>
              <g transform="translate(25,5)">
                <path d="M11,0 C4.9,0 0,4.9 0,11 C0,17.1 11,28 11,28 C11,28 22,17.1 22,11 C22,4.9 17.1,0 11,0 Z" fill="white" opacity="0.85"/>
                <circle cx="11" cy="10" r="5" fill="url(#mLg2)"/>
                <circle cx="11" cy="10" r="2.5" fill="white"/>
              </g>
            </svg>
            <span className="font-display text-lg font-bold text-navy">Mouna</span>
          </div>
          <button onClick={() => setOpen(false)}
            className="text-gray-400 text-xl w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100">✕</button>
        </div>

        {/* Profile block */}
        <div className="px-5 pb-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-mouna-blue to-teal flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
              {initial}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-navy truncate">{displayName}</div>
              <div className="text-xs text-gray-500">{accountLabel}</div>
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="flex-1 overflow-y-auto py-2">
          {sections.map(section => (
            <div key={section.title} className="mb-2">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wider px-5 py-2.5">{section.title}</div>
              {section.items.map(item => (
                <button key={item.path + item.label} onClick={() => go(item.path)}
                  className="w-full flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors text-left">
                  <span className="text-lg w-6 text-center">{item.icon}</span>
                  <span className="text-sm font-medium text-navy">{item.label}</span>
                </button>
              ))}
            </div>
          ))}

          {/* Language & Appearance inline */}
          <div className="px-5 py-2.5 flex items-center justify-between hover:bg-gray-50">
            <div className="flex items-center gap-3">
              <span className="text-lg w-6 text-center">🌍</span>
              <span className="text-sm font-medium text-navy">Language</span>
            </div>
            <LanguageSelector />
          </div>
          <div className="px-5 py-2.5 flex items-center justify-between hover:bg-gray-50">
            <div className="flex items-center gap-3">
              <span className="text-lg w-6 text-center">🌙</span>
              <span className="text-sm font-medium text-navy">Appearance</span>
            </div>
            <ThemeToggle />
          </div>
          <div className="px-5 py-2.5 flex items-center justify-between hover:bg-gray-50">
            <div className="flex items-center gap-3">
              <span className="text-lg w-6 text-center">🎨</span>
              <span className="text-sm font-medium text-navy">Background</span>
            </div>
            <BackgroundPicker />
          </div>

          {/* Sign Out section inside scrollable area */}
          <div className="mt-4 mx-3 mb-6 border-t border-gray-100 pt-4">
            <button onClick={handleSignOut}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-red-50 text-red-500 transition-colors bg-red-50 bg-opacity-50">
              <span className="text-lg w-6 text-center">🚪</span>
              <span className="text-sm font-bold">Sign Out</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
